import React, { useCallback, useEffect, useRef, useState } from "react";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import { printElement } from "../master/Print";
import { messEnrollList, messList } from "../../api/Mess";

export default function StudentsModal({ type }) {
  const [loading, setLoading] = useState(false);
  const [messes, setMesses] = useState([]);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    type: type,
    mess_id: "",
    search: "",
  });

  const modalRef = useRef(null);
  const isModalOpenRef = useRef(false);

  /*
   * ------------------------------------------------------------
   * Fetch Mess List
   * Only called when modal is opened
   * ------------------------------------------------------------
   */
  const getMessList = useCallback(async () => {
    try {
      const response = await messList("&limit=all");

      if (response?.data?.status) {
        setMesses(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    }
  }, []);

  /*
   * ------------------------------------------------------------
   * Fetch Student / Staff Enrollment List
   * Only called when modal is open
   * ------------------------------------------------------------
   */
  const getDataList = useCallback(async () => {
    if (!isModalOpenRef.current) {
      return;
    }

    setLoading(true);

    try {
      let addUrl = "limit=all";

      Object.keys(filters).forEach((key) => {
        const value = filters[key];

        if (value !== undefined && value !== null && value !== "") {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      const response = await messEnrollList(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || []);
      } else {
        setData([]);

        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      setData([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /*
   * ------------------------------------------------------------
   * Modal Open / Close Handler
   * ------------------------------------------------------------
   */
  useEffect(() => {
    const modalElement = document.getElementById("StudentData");

    if (!modalElement) return;

    const handleModalOpen = () => {
      isModalOpenRef.current = true;

      // Set current type whenever modal opens
      setFilters({
        type,
        mess_id: "",
        search: "",
      });

      // Fetch mess dropdown only when modal opens
      getMessList();
    };

    const handleModalClose = () => {
      isModalOpenRef.current = false;

      // Clear old data so previous Student/Staff data
      // is not visible when modal is opened again.
      setData([]);

      setFilters({
        type,
        mess_id: "",
        search: "",
      });
    };

    modalElement.addEventListener("shown.bs.modal", handleModalOpen);

    modalElement.addEventListener("hidden.bs.modal", handleModalClose);

    return () => {
      modalElement.removeEventListener("shown.bs.modal", handleModalOpen);

      modalElement.removeEventListener("hidden.bs.modal", handleModalClose);
    };
  }, [type, getMessList]);

  /*
   * ------------------------------------------------------------
   * Fetch enrollment list whenever filters change,
   * BUT ONLY when modal is actually open.
   * ------------------------------------------------------------
   */
  useEffect(() => {
    if (!isModalOpenRef.current) {
      return;
    }

    getDataList();
  }, [filters.type, filters.mess_id, filters.search, getDataList]);

  /*
   * ------------------------------------------------------------
   * Change Type
   * ------------------------------------------------------------
   */
  useEffect(() => {
    if (isModalOpenRef.current) {
      setFilters((prev) => ({
        ...prev,
        type,
      }));
    }
  }, [type]);

  /*
   * ------------------------------------------------------------
   * Search
   * ------------------------------------------------------------
   */
  const handleSearch = (value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
    }));
  };

  /*
   * ------------------------------------------------------------
   * Mess Filter
   * ------------------------------------------------------------
   */
  const handleMessChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      mess_id: value,
    }));
  };

  /*
   * ------------------------------------------------------------
   * Reset Filters
   * ------------------------------------------------------------
   */
  const resetFilters = () => {
    setFilters({
      type,
      mess_id: "",
      search: "",
    });
  };

  const activeFilterCount = [filters?.mess_id, filters?.search].filter(
    Boolean,
  ).length;

  const handlePrint = () => {
    if (!data?.length) {
      SweetAlertShow(
        "info",
        "Nothing to Print",
        `No ${type?.toLowerCase()} records are available to print.`,
      );
      return;
    }

    const selectedMess = filters?.mess_id
      ? messes?.find((mess) => String(mess?.id) === String(filters?.mess_id))
          ?.name
      : "All Messes";

    const searchText = filters?.search?.trim() || "All";

    const printWindow = window.open("", "_blank", "width=1200,height=800");

    if (!printWindow) {
      SweetAlertShow(
        "warning",
        "Popup Blocked",
        "Please allow pop-ups to print the list.",
      );
      return;
    }

    const rows = data
      .map(
        (student, index) => `
        <tr>
          <td class="serial">${index + 1}</td>

          <td>
            <div class="name">
              ${student?.name || "-"}
            </div>
            ${
              student?.id ? `<div class="sub-text">ID #${student.id}</div>` : ""
            }
          </td>

          ${
            type === "Student"
              ? `
                <td>
                  ${student?.father_name || "-"}
                </td>
              `
              : ""
          }

          <td>
            ${student?.mobile || "-"}
          </td>

          <td class="address">
            ${student?.address || "-"}
          </td>

          <td>
            <span class="mess-badge">
              ${student?.mess?.name || "-"}
            </span>
          </td>
        </tr>
      `,
      )
      .join("");

    const currentDate = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const currentTime = new Date().toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });

    printWindow.document.open();

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>
          ${type === "Student" ? "Mess Students" : "Mess Staff"}
        </title>

        <meta charset="UTF-8" />

        <style>

          @page {
            size: A4 landscape;
            margin: 12mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 0;
            font-family:
              Arial,
              Helvetica,
              sans-serif;
            color: #222;
            background: #fff;
            font-size: 11px;
          }

          .print-container {
            width: 100%;
          }

          /* HEADER */

          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding-bottom: 12px;
            border-bottom: 2px solid #333;
            margin-bottom: 12px;
          }

          .title-section h1 {
            margin: 0 0 4px 0;
            font-size: 20px;
            font-weight: 700;
          }

          .title-section .subtitle {
            margin: 0;
            color: #666;
            font-size: 11px;
          }

          .print-info {
            text-align: right;
            color: #555;
            font-size: 10px;
            line-height: 1.6;
          }

          /* SUMMARY */

          .summary {
            display: flex;
            gap: 10px;
            margin-bottom: 14px;
          }

          .summary-box {
            border: 1px solid #ddd;
            border-radius: 5px;
            padding: 8px 12px;
            min-width: 150px;
          }

          .summary-label {
            display: block;
            color: #777;
            font-size: 9px;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            margin-bottom: 3px;
          }

          .summary-value {
            font-size: 11px;
            font-weight: 600;
          }

          /* TABLE */

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          thead {
            display: table-header-group;
          }

          tfoot {
            display: table-footer-group;
          }

          tr {
            page-break-inside: avoid;
          }

          th {
            background: #f1f3f5;
            border: 1px solid #bbb;
            padding: 8px 7px;
            text-align: left;
            font-size: 10px;
            font-weight: 700;
            color: #222;
          }

          td {
            border: 1px solid #ddd;
            padding: 7px;
            vertical-align: top;
            font-size: 10px;
            line-height: 1.4;
          }

          tbody tr:nth-child(even) {
            background: #fafafa;
          }

          .serial {
            width: 4%;
            text-align: center;
          }

          .name {
            font-weight: 600;
          }

          .sub-text {
            color: #777;
            font-size: 8px;
            margin-top: 2px;
          }

          .address {
            max-width: 300px;
            word-break: break-word;
          }

          .mess-badge {
            display: inline-block;
            padding: 3px 7px;
            border: 1px solid #ccc;
            border-radius: 12px;
            font-size: 9px;
            white-space: nowrap;
          }

          /* FOOTER */

          .footer {
            margin-top: 15px;
            padding-top: 8px;
            border-top: 1px solid #ccc;
            display: flex;
            justify-content: space-between;
            color: #666;
            font-size: 9px;
          }

          @media print {

            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .no-print {
              display: none !important;
            }

          }

        </style>
      </head>

      <body>

        <div class="print-container">

          <!-- HEADER -->

          <div class="header">

            <div class="title-section">

              <h1>
                ${type === "Student" ? "Mess Students" : "Mess Staff"}
              </h1>

              <div class="subtitle">
                Mess Enrollment Report
              </div>

            </div>

            <div class="print-info">

              <div>
                <strong>Generated:</strong>
                ${currentDate} ${currentTime}
              </div>

              <div>
                <strong>Total Records:</strong>
                ${data.length}
              </div>

            </div>

          </div>

          <!-- SUMMARY -->

          <div class="summary">

            <div class="summary-box">

              <span class="summary-label">
                Mess
              </span>

              <span class="summary-value">
                ${selectedMess}
              </span>

            </div>

            <div class="summary-box">

              <span class="summary-label">
                Search
              </span>

              <span class="summary-value">
                ${searchText}
              </span>

            </div>

            <div class="summary-box">

              <span class="summary-label">
                Type
              </span>

              <span class="summary-value">
                ${type}
              </span>

            </div>

            <div class="summary-box">

              <span class="summary-label">
                Records
              </span>

              <span class="summary-value">
                ${data.length}
              </span>

            </div>

          </div>

          <!-- TABLE -->

          <table>

            <thead>

              <tr>

                <th style="width: 4%; text-align:center;">
                  #
                </th>

                <th style="width: 20%;">
                  Name
                </th>

                ${
                  type === "Student"
                    ? `
                      <th style="width: 18%;">
                        Father Name
                      </th>
                    `
                    : ""
                }

                <th style="width: 15%;">
                  Mobile
                </th>

                <th style="width: 28%;">
                  Address
                </th>

                <th style="width: 15%;">
                  Mess
                </th>

              </tr>

            </thead>

            <tbody>

              ${rows}

            </tbody>

          </table>

          <!-- FOOTER -->

          <div class="footer">

            <span>
              Mess Management System
            </span>

            <span>
              Total ${type?.toLowerCase()}:
              <strong>${data.length}</strong>
            </span>

          </div>

        </div>

        <script>

          window.onload = function () {

            setTimeout(function () {

              window.print();

            }, 300);

          };

          window.onafterprint = function () {

            setTimeout(function () {

              window.close();

            }, 300);

          };

        </script>

      </body>

    </html>
  `);

    printWindow.document.close();
  };

  return (
    <>
      <div
        className="modal fade"
        id="StudentData"
        tabIndex={-1}
        aria-labelledby="studentDataLabel"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content border-0">
            {/* =====================================================
                HEADER
            ====================================================== */}
            <div className="modal-header border-bottom px-4 py-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center ${
                    type === "Student" ? "bg-label-primary" : "bg-label-danger"
                  }`}
                  style={{
                    width: 48,
                    height: 48,
                  }}
                >
                  <i
                    className={`${
                      type === "Student" ? "ri-group-line" : "ri-user-star-line"
                    } ri-24px ${
                      type === "Student" ? "text-primary" : "text-danger"
                    }`}
                  />
                </div>

                <div>
                  <h5
                    className="modal-title fw-semibold mb-1"
                    id="studentDataLabel"
                  >
                    {type === "Student" ? "Mess Students" : "Mess Staff"}
                  </h5>

                  <small className="text-muted">
                    View and filter {type?.toLowerCase()} enrolled in mess
                    facilities
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            {/* =====================================================
                BODY
            ====================================================== */}
            <div className="modal-body bg-body-tertiary px-4 py-4">
              {/* FILTER CARD */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-transparent border-bottom py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 fw-semibold">Filters</h6>

                      <small className="text-muted">
                        Filter {type?.toLowerCase()} by mess or search by name
                      </small>
                    </div>

                    {activeFilterCount > 0 && (
                      <span className="badge bg-label-primary rounded-pill">
                        {activeFilterCount} Active
                      </span>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  <div className="row g-3 align-items-center">
                    {/* MESS */}
                    <div className="col-xl-4 col-lg-5 col-md-6">
                      <div className="form-floating form-floating-outline">
                        <select
                          name="mess_id"
                          id="mess_id"
                          className="form-select"
                          value={filters?.mess_id || ""}
                          onChange={(e) => handleMessChange(e.target.value)}
                        >
                          <option value="">All Messes</option>

                          {messes?.map((mess) => (
                            <option value={mess?.id} key={mess?.id}>
                              {mess?.name}
                            </option>
                          ))}
                        </select>

                        <label htmlFor="mess_id">Select Mess</label>
                      </div>
                    </div>

                    {/* SEARCH */}
                    <div className="col-xl-5 col-lg-5 col-md-6">
                      <div className="form-floating form-floating-outline position-relative">
                        <input
                          type="text"
                          className="form-control"
                          name="search"
                          id="messSearch"
                          placeholder={`Search ${type}`}
                          value={filters?.search || ""}
                          onChange={(e) => handleSearch(e.target.value)}
                        />

                        <label htmlFor="messSearch">Search {type}</label>

                        <i
                          className="ri-search-line position-absolute text-muted"
                          style={{
                            right: 15,
                            top: 18,
                          }}
                        />
                      </div>
                    </div>

                    {/* ACTIONS */}
                    <div className="col-xl-3 col-lg-2 col-md-12">
                      <div className="d-flex gap-2 justify-content-lg-end">
                        <button
                          type="button"
                          onClick={resetFilters}
                          className="btn btn-label-danger"
                          title="Reset Filters"
                        >
                          <i className="ri-restart-line ri-18px me-1" />
                          Reset
                        </button>

                        <button
                          type="button"
                          onClick={handlePrint}
                          className="btn btn-label-primary"
                          title="Print"
                        >
                          <i className="ri-printer-line ri-18px me-1" />
                          Print
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* TABLE */}
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent border-bottom py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 fw-semibold">{type} Enrollment</h6>

                      <small className="text-muted">
                        {data?.length || 0} record
                        {data?.length === 1 ? "" : "s"} found
                      </small>
                    </div>

                    {loading && (
                      <div className="d-flex align-items-center gap-2 text-muted">
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                        Loading...
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-body p-0">
                  <div className="table-responsive" id="PrintDiv">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr className="text-nowrap">
                          <th className="ps-4" style={{ width: 70 }}>
                            #
                          </th>

                          <th>Name</th>

                          {type === "Student" && <th>Father Name</th>}

                          <th>Mobile</th>

                          <th>Address</th>

                          <th>Mess</th>

                          <th className="text-end pe-4" style={{ width: 100 }}>
                            Action
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={type === "Student" ? 7 : 6}>
                              <div className="text-center py-5">
                                <span
                                  className="spinner-border text-primary mb-3"
                                  role="status"
                                />

                                <div className="text-muted">
                                  Loading {type?.toLowerCase()}...
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : data?.length > 0 ? (
                          data.map((student, index) => (
                            <tr key={student?.id || index}>
                              <td className="ps-4 text-muted fw-medium">
                                {index + 1}
                              </td>

                              <td>
                                <div className="d-flex align-items-center gap-3">
                                  <div
                                    className={`rounded-circle d-flex align-items-center justify-content-center fw-semibold ${
                                      type === "Student"
                                        ? "bg-label-primary text-primary"
                                        : "bg-label-danger text-danger"
                                    }`}
                                    style={{
                                      width: 40,
                                      height: 40,
                                      minWidth: 40,
                                    }}
                                  >
                                    {student?.name
                                      ?.trim()
                                      ?.charAt(0)
                                      ?.toUpperCase() || "U"}
                                  </div>

                                  <div>
                                    <div className="fw-semibold">
                                      {student?.name || "-"}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {type === "Student" && (
                                <td>
                                  <span className="text-muted">
                                    {student?.father_name || "-"}
                                  </span>
                                </td>
                              )}

                              <td>
                                {student?.mobile ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="avatar avatar-xs">
                                      <span className="avatar-initial rounded bg-label-success">
                                        <i className="ri-phone-line ri-14px" />
                                      </span>
                                    </span>

                                    <span>{student.mobile}</span>
                                  </div>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>

                              <td>
                                <div
                                  className="text-muted"
                                  style={{
                                    maxWidth: 280,
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {student?.address ? (
                                    <>
                                      <i className="ri-map-pin-line text-primary me-1" />
                                      {student.address}
                                    </>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              </td>

                              <td>
                                {student?.mess?.name ? (
                                  <span className="badge bg-label-warning">
                                    <i className="ri-restaurant-2-line me-1" />
                                    {student.mess.name}
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>

                              <td className="text-end pe-4">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-text-primary rounded-pill"
                                  title="View"
                                >
                                  <i className="ri-eye-line ri-18px" />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={type === "Student" ? 7 : 6}>
                              <div className="text-center py-5">
                                <div
                                  className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center mx-auto mb-3"
                                  style={{
                                    width: 70,
                                    height: 70,
                                  }}
                                >
                                  <i
                                    className={`${
                                      type === "Student"
                                        ? "ri-group-line"
                                        : "ri-user-star-line"
                                    } ri-32px text-secondary`}
                                  />
                                </div>

                                <h6 className="mb-1">No {type} Found</h6>

                                <p className="text-muted mb-0">
                                  No {type?.toLowerCase()} enrollment matches
                                  the selected filters.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* =====================================================
                FOOTER
            ====================================================== */}
            <div className="modal-footer border-top px-4 py-3">
              <div className="me-auto text-muted small">
                Showing <strong>{data?.length || 0}</strong>{" "}
                {type?.toLowerCase()} record
                {data?.length === 1 ? "" : "s"}
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
